// One place to switch between registers: a row of register tabs at the top of the Registre page,
// each with its own counts, plus the per-register stats hook that the sidebar's single "Registre" item sums up.
import React, { useEffect, useRef, useState } from 'react';
import { api, qs } from '../api';
import { useCatalog } from '../catalog';
import { navigate } from '../store';
import type { DocType, Stats } from '../types';
import { Icon } from '../ui/Icon';
import { Combobox } from '../ui';

export type RegCounts = { total: number; inWork: number; expiring: number; overdue: number };

const LAST_KEY = 'docflow.lastRegister';
export const rememberRegister = (id: number) => { try { localStorage.setItem(LAST_KEY, String(id)); } catch { /* ignore */ } };
export const lastRegister = (): string | null => { try { return localStorage.getItem(LAST_KEY); } catch { return null; } };

/** Registers in the order they were created: the three built-in ones (Intrări-Ieșiri, Petiții, Legea 544) first, then new ones as they are added. */
export const orderRegisters = (types: DocType[]) => types.filter(t => t.isRegistry).sort((a, b) => a.id - b.id);

/** The register's own icon, as set in Administrare → Tipuri de documente. */
export const registerIcon = (t: Pick<DocType, 'kind' | 'icon'>) => t.icon || (t.kind === 'PETITIONS' ? 'users' : t.kind === 'L544' ? 'info' : 'inbox');

/** Counts for every register, refreshed every minute and whenever documents change. */
export function useRegisterStats(regs: DocType[]): Record<number, RegCounts> {
  const [stats, setStats] = useState<Record<number, RegCounts>>({});
  const ids = regs.map(t => t.id).join(',');
  useEffect(() => {
    if (!regs.length) return;
    const refresh = () => Promise.all(regs.map(t => api.get<Stats>('/api/documents/stats' + qs({ registry: true, typeId: t.id }))
      .then(s => [t.id, { total: s.counts.total || 0, inWork: (s.counts.in_work || 0) + (s.counts.received || 0), expiring: s.counts.expiring || 0, overdue: s.counts.overdue || 0 }] as const)
      .catch(() => [t.id, { total: 0, inWork: 0, expiring: 0, overdue: 0 }] as const)))
      .then(rows => setStats(Object.fromEntries(rows)));
    refresh();
    const timer = setInterval(refresh, 60000);
    window.addEventListener('docflow:changed', refresh);
    return () => { clearInterval(timer); window.removeEventListener('docflow:changed', refresh); };
  }, [ids]);
  return stats;
}

const GAP = 10, MIN_TAB = 190, MAX_PER_VIEW = 5;
const FOCUS_EVENT = 'docflow:register-focus';

/** The register tabs at the top of the Registre page: one row, at most 5 visible; arrows slide through the rest. */
export function RegisterSwitcher({ current }: { current: number | null }) {
  const { types } = useCatalog();
  const regs = orderRegisters(types);
  const stats = useRegisterStats(regs);
  const track = useRef<HTMLDivElement>(null);
  const [perView, setPerView] = useState(MAX_PER_VIEW);
  const [edge, setEdge] = useState({ start: true, end: true });
  const many = regs.length > perView;

  const tabAt = (i: number) => track.current?.children[i] as HTMLElement | undefined;
  /** scroll position that puts tab i first in the row */
  const offsetOf = (i: number) => { const t = tabAt(i), first = tabAt(0); return t && first ? t.offsetLeft - first.offsetLeft : 0; };
  const step = () => regs.length > 1 ? offsetOf(1) : 0;
  const updateEdges = () => { const el = track.current; if (el) setEdge({ start: el.scrollLeft <= 2, end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 2 }); };
  /** slide so that register `id` is the first one visible (as far as the row allows) */
  const slideTo = (id: number, smooth = true) => {
    const el = track.current, i = regs.findIndex(t => t.id === id);
    if (el && i >= 0) el.scrollTo({ left: offsetOf(i), behavior: smooth ? 'smooth' : 'auto' });
  };
  const isVisible = (id: number) => {
    const el = track.current, i = regs.findIndex(t => t.id === id);
    if (!el || i < 0) return true;
    const left = offsetOf(i), right = left + (tabAt(i)?.offsetWidth || 0);
    return left >= el.scrollLeft - 2 && right <= el.scrollLeft + el.clientWidth + 2;
  };

  // how many tabs fit: at most 5, fewer on narrow screens
  useEffect(() => {
    const el = track.current; if (!el) return;
    const ro = new ResizeObserver(() => { setPerView(Math.max(1, Math.min(MAX_PER_VIEW, Math.floor((el.clientWidth + GAP) / (MIN_TAB + GAP))))); updateEdges(); });
    ro.observe(el);
    return () => ro.disconnect();
  }, [regs.length > 1]);
  useEffect(() => { updateEdges(); }, [perView, regs.length]);
  // the open register is always in view (e.g. opened from the sidebar or from a link)
  useEffect(() => { if (current != null && !isVisible(current)) slideTo(current, false); }, [current, perView, regs.length]);
  // chosen in the header selector: slide so it is the first one in the row
  useEffect(() => {
    const on = (e: Event) => slideTo(Number((e as CustomEvent).detail));
    window.addEventListener(FOCUS_EVENT, on);
    return () => window.removeEventListener(FOCUS_EVENT, on);
  }, [regs.map(t => t.id).join(','), perView]);

  if (regs.length === 0) return null;
  const slide = (dir: 1 | -1) => track.current?.scrollBy({ left: dir * step(), behavior: 'smooth' });
  return (
    <div className={'reg-carousel' + (many ? ' many' : '')}>
      {many && <button type="button" className="reg-arrow" onClick={() => slide(-1)} disabled={edge.start} aria-label="Registrele anterioare" title="Registrele anterioare"><Icon name="chevronLeft" size={20} /></button>}
      <div className="reg-switch" ref={track} onScroll={updateEdges} role="tablist" aria-label="Registre"
        style={{ ['--per-view' as any]: Math.min(perView, regs.length) }}>
        {regs.map(t => {
          const s = stats[t.id];
          const active = t.id === current;
          return (
            <button key={t.id} role="tab" aria-selected={active} className={'reg-tab' + (active ? ' active' : '')}
              style={{ ['--reg-color' as any]: t.color || 'var(--accent)' }}
              title={[t.name, t.legalBasis || t.description].filter(Boolean).join('\n')}
              onClick={() => { rememberRegister(t.id); navigate('/registry/' + t.id); }}>
              <span className="reg-tab-icon"><Icon name={registerIcon(t)} size={18} /></span>
              <span className="reg-tab-text">
                <b>{t.name}</b>
                <span className="reg-tab-meta">
                  {s ? <>{s.total} {s.total === 1 ? 'document' : 'documente'}{s.inWork > 0 && <> · {s.inWork} deschise</>}</> : 'Se încarcă…'}
                </span>
              </span>
              {s && s.overdue > 0 && <span className="reg-tab-badge danger" title={`${s.overdue} cu termenul depășit`}>{s.overdue} depășite</span>}
              {s && s.overdue === 0 && s.expiring > 0 && <span className="reg-tab-badge warn" title={`${s.expiring} aproape de termen`}>{s.expiring} la termen</span>}
            </button>
          );
        })}
      </div>
      {many && <button type="button" className="reg-arrow" onClick={() => slide(1)} disabled={edge.end} aria-label="Registrele următoare" title="Registrele următoare"><Icon name="chevronRight" size={20} /></button>}
    </div>
  );
}

/** Header selector: every register the user can work in; typing narrows the list; choosing one opens it
 *  and slides the register row so that it is the first one. */
export function RegisterPicker({ current }: { current: number | null }) {
  const { types } = useCatalog();
  const regs = orderRegisters(types);
  if (regs.length <= 1) return null;
  return (
    <div className="reg-picker" title="Alegeți registrul — tastați o parte din nume">
      <Combobox value={current ?? undefined} clearable={false} placeholder="Caută registrul…"
        options={regs.map(t => ({ value: t.id, label: t.name, color: t.color }))}
        onChange={v => {
          if (v == null || v === '') return;
          const id = Number(v);
          rememberRegister(id);
          navigate('/registry/' + id);
          window.dispatchEvent(new CustomEvent(FOCUS_EVENT, { detail: id }));
        }} />
    </div>
  );
}

/** The name on a register card, without the leading "Registru" / "Registrul" (the full name stays in the tooltip and everywhere else). */
